<template>
    <form-wrapper ref="form"
                  v-slot="{ invalid, loading }"
                  class="expired-lot-business-order-create-form"
                  :in-modal="true"
                  :scroll-on-error="true"
                  @submit="save"
    >
        <p class="expired-lot-business-order-create-form__disclaimer">
            {{ $t('expiredInventory.businessOrders.disclaimerLine') }}
        </p>

        <component
            :is="isTabletSize ? 'div' : 'base-scroll-container'"
            :class="''"
            is-in-modal
        >
            <div class="expired-lot-business-order-create-form__contorl">
                <base-switch id="disposalSwitch"
                             :value="disposalSwitch"
                             name="disposalSwitch"
                             :options="disposalOptions"
                             @change="changeDisposalSwitch"
                />
            </div>
            <collapsible-block
                ref="collapsible"
                :class="''"
                :title-text="$t('businessOrders.labels.orderInformation')"
                preview-only
            >
                <flex-row :class="''">
                    <div class="field col"
                         style="margin-right: 20px;"
                    >
                        <form-input id="reference-number"
                                    v-model.trim="model.referenceNumber"
                                    name="reference-number"
                                    :rules="rules.referenceNumber"
                                    :label="$t('businessOrders.labels.referenceNo')"
                        />
                    </div>
                    <div
                        v-if="!warehouses || warehouses.length != 2"
                        class="field col"
                    >
                        <form-select
                            id="orderWarehouseId"
                            :value="model.warehouseName"
                            name="warehouseName"
                            rules="required"
                            dropdown-longer-than-input
                            :label="$t('businessOrders.warehouse.shipFrom')"
                            placeholder=""
                            :options="warehouses"
                            :searchable="false"
                            option-label="warehouseName"
                            value-field="warehouseId"
                            track-by="warehouseId"
                            @input="handleWarehouseSelection"
                        />
                    </div>
                </flex-row>

                <bulk-items-lot-table
                    id="items-table"
                    :class="''"
                    :validity="validity"
                    :item-parent="model"
                    :is-visible-record-serial-number="true"
                    :invalid-items-count="invalidItemsCount"
                    :selected-warehouse="selectedWarehouseName"
                    @delete-item="(row) => deleteItem(row)"
                    @open-edit-item="(item) => onOpenEditItem(item)"
                />

                <flex-row :class="''">
                    <div class="field col">
                        <form-textarea id="order-comments"
                                       v-model.trim="model.orderComments"
                                       name="order-comments"
                                       :required="disposalSwitch === 1"
                                       :rules="rules.orderComments"
                                       :label="$t('businessOrders.labels.orderComments')"
                        />
                    </div>
                </flex-row>
            </collapsible-block>
            <collapsible-block
                ref="collapsible"
                :class="''"
                :title-text="$t('businessOrders.labels.consigneeInformation')"
                preview-only
            >
                <flex-row>
                    <div class="field col">
                        <form-input id="consignee-name"
                                    v-model.trim="model.customerName"
                                    name="consignee-name"
                                    :rules="rules.consigneeName"
                                    :label="$t('businessOrders.labels.consigneeName')"
                                    :disabled="isEditingEdi"
                        />
                    </div>
                </flex-row>
                <flex-row>
                    <div class="field col"
                         style="margin-right: 20px;"
                    >
                        <form-input id="consignee-phone"
                                    v-model.trim="model.customerPhoneNumber"
                                    name="consignee-phone"
                                    :rules="model.country === 'US' ? rules.consigneePhone
                                        : rules.consigneePhoneRequired"
                                    :label="$t('businessOrders.labels.consigneePhone')"
                                    :disabled="isEditingEdi"
                        />
                    </div>
                    <div class="field col">
                        <form-input id="consignee-email"
                                    v-model.trim="model.customerEmail"
                                    name="consignee-email"
                                    :rules="model.country === 'US' ? rules.consigneeEmail
                                        : rules.consigneeEmailRequired"
                                    :label="$t('businessOrders.labels.consigneeEmail')"
                                    :disabled="isEditingEdi"
                        />
                    </div>
                </flex-row>
                <flex-row>
                    <div class="field col">
                        <form-input id="consignee-address-line-1"
                                    v-model.trim="model.streetAddress1"
                                    name="consignee-address-line-1"
                                    :rules="rules.consigneeAddressLine1"
                                    :label="$t('businessOrders.labels.consigneeAddressLine1')"
                                    :disabled="isEditingEdi"
                        >
                            <template slot="labelTooltip">
                                <button-tooltip
                                    v-tooltip.bottom="{
                                        content: $t('businessOrders.labels.consigneeAddressLine1Tooltip'),
                                        delay: { show: 500, hide: 300 },
                                    }"
                                />
                            </template>
                        </form-input>
                    </div>
                </flex-row>
                <flex-row v-if="showSecondAddressLine">
                    <div class="field col">
                        <form-input id="consignee-address-line-2"
                                    v-model.trim="model.streetAddress2"
                                    name="consignee-address-line-2"
                                    :rules="rules.consigneeAddressLine2"
                                    :label="$t('businessOrders.labels.consigneeAddressLine2')"
                                    :disabled="isEditingEdi"
                        />
                    </div>
                </flex-row>
                <flex-row v-else>
                    <div class="col col--on-left">
                        <base-button id="add-address-line"
                                     as-link
                                     :disabled="isEditingEdi"
                                     @click="addAddressLine"
                        >
                            <svg slot="iconLeft"
                                 width="20"
                                 height="20"
                            >
                                <use xlink:href="#plus-round" />
                            </svg>
                            {{ $t('businessOrders.labels.consigneeAddAddressLine') }}
                        </base-button>
                    </div>
                </flex-row>
                <flex-row>
                    <div class="field col"
                         style="margin-right: 20px;"
                    >
                        <form-input id="city"
                                    v-model.trim="model.city"
                                    name="city"
                                    :rules="rules.city"
                                    :label="$t('general.addressLabels.city')"
                                    :disabled="isEditingEdi"
                        />
                    </div>
                    <div class="field col">
                        <form-input id="state-province"
                                    v-model.trim="model.stateOrProvince"
                                    name="state-province"
                                    :rules="rules.stateProvince"
                                    :label="$t('businessOrders.labels.stateProvince')"
                                    :disabled="isEditingEdi"
                        />
                    </div>
                </flex-row>
                <flex-row>
                    <div class="field col col--50-percent"
                         style="margin-right: 20px;"
                    >
                        <form-input id="zip-code"
                                    v-model.trim="model.zipCode"
                                    name="zip-code"
                                    :rules="model.country === 'US' ? rules.zipCodeRequired : rules.zipCode"
                                    :label="$t('general.addressLabels.zipCode')"
                                    :disabled="isEditingEdi"
                        />
                    </div>
                    <div class="field col col--50-percent">
                        <form-select id="origin-country"
                                     :value="model.country"
                                     name="country-region"
                                     rules="required|min:1|max:3"
                                     dropdown-longer-than-input
                                     :label="$t('businessOrders.labels.countryRegion')"
                                     :options="countryCodes"
                                     :searchable="true"
                                     :disabled="isEditingEdi"
                                     option-label="name"
                                     value-field="code"
                                     track-by="name"
                                     @input="(value) => { model.country = value }"
                        />
                    </div>
                </flex-row>
            </collapsible-block>
            <collapsible-block
                ref="collapsible"
                :title-text="$t('businessOrders.labels.handlingUnits')"
                disabled
            />
            <collapsible-block
                ref="collapsible"
                :title-text="$t('businessOrders.labels.uploadShippingLabel')"
                disabled
            />
            <collapsible-block
                ref="collapsible"
                :title-text="$t('businessOrders.labels.uploadBillOfLanding')"
                disabled
            />
            <collapsible-block
                ref="collapsible"
                :title-text="$t('businessOrders.labels.uploadShippingDocument')"
                disabled
            />
        </component>
        <div class="modal__actions modal__actions--with-scroll">
            <base-button id="cancelCreateBtn"
                         for-modal
                         @click="cancel"
            >
                {{ $t('general.cancel') }}
            </base-button>
            <base-button id="createItemBtnModal"
                         for-modal
                         type="submit"
                         :disabled="invalid || loading || !orderHasValidItems"
            >
                {{ $t('general.save') }}
            </base-button>
        </div>
        <edit-item-popup
            v-if="itemForEdit"
            :items-list="initialSearchItems"
            :item-parent="model"
            :item-for-edit="itemForEdit"
            :is-sku-edit-disabled="true"
            :selected-warehouse="selectedWarehouseName"
            :is-visible-record-serial-number="true"
            :is-display-lot-detail="true"
            @cancel-editing-item="cancelEditingItem"
            @save-editing-item="(item) => saveEditingItem(item)"
        />
    </form-wrapper>
</template>
<script>
import '@/assets/icons/plus-round.svg';
import '@/assets/icons/plus.svg';
import { $t } from '@/translations';
import { OrderTypes } from '@/core/enums';
import BaseButton from '@/components/base/BaseButton';
import { BaseSwitch } from '@/components/base';
import {
    FieldWrapper, FormInput, FormSelect, FormWrapper,
} from '@/components/form';
import CollapsibleBlock from '@/components/layout/CollapsibleBlock';
import BaseScrollContainer from '@/components/base/BaseScrollContainer';
import BaseIconButton from '@/components/base/BaseIconButton';
import FormTextarea from '@/components/form/FormTextarea';
import BulkItemsLotTable from '@/components/bulk-items-list/BulkItemsLotTable';
import SearchItem from '@/components/bulk-items-list/SearchItem';
import EditItemPopup from '@/components/bulk-items-list/EditItemPopup';
import SkuBulkUpload from '@/components/bulk-items-list/SkuBulkUpload';
import ItemsBulkValidator from '@/components/bulk-items-list/ItemsBulkValidator';
import ButtonTooltip from '@/components/buttons/ButtonTooltip';
import countriesService from '@/services/countries-service';
import warehouseService from '@/services/warehouses-service';
import settingsService from '@/services/settings-service';
import BusinessOrderManualSelectionModal from '@/views/business-orders/BusinessOrderManualSelectionModal';
import validationRules from './validation-rules';
import { sanitize } from '../items/itemSanitizer';

const validator = new ItemsBulkValidator();

export default {
    name: 'ExpiredLotBusinessOrderCreateForm',
    components: {
        FormTextarea,
        FormWrapper,
        FormInput,
        FormSelect,
        FieldWrapper,
        BaseButton,
        BaseSwitch,
        CollapsibleBlock,
        BaseScrollContainer,
        BaseIconButton,
        BulkItemsLotTable,
        SearchItem,
        EditItemPopup,
        SkuBulkUpload,
        ButtonTooltip,
    },
    props: {
        businessOrder: {
            type: Object,
            default: () => ({}),
        },
        isSmartFreightSelected: {
            type: Boolean,
            default: false,
        },
        expiredItems: {
            type: Array,
            required: true,
            default: () => [],
        },
        warehouses: {
            type: Array,
            required: true,
            default: () => [],
        },
    },
    data() {
        return {
            disposalOptions: [
                {
                    label: $t('expiredInventory.businessOrders.disposal'),
                    desc: $t('expiredInventory.businessOrders.disposal'),
                    value: 1,
                },
                {
                    label: $t('expiredInventory.businessOrders.nonDisposal'),
                    desc: $t('expiredInventory.businessOrders.nonDisposal'),
                    value: 0,
                },
            ],
            id: 0,
            showSecondAddressLine: false,
            model: this.prepareModel(this.businessOrder),
            countryCodes: [{ id: 1, code: 'US', name: 'United States' }],
            shipperContactInformation: null,
            validity: [],
            invalidItemsCount: 0,
            itemForEdit: null,
            initialSearchItems: null,
            selectedWarehouseName: null,
            disposalSwitch: 0,
        };
    },
    computed: {
        rules() {
            return validationRules.createRules;
        },
        isTabletSize() {
            return this.$mq === 'tablet' || this.$mq === 'mobile';
        },
        isMoreThanOneItem() {
            return this.model.items.length > 1;
        },
        orderHasValidItems() {
            return this.model.items.length > 0 && this.invalidItemsCount === 0;
        },
        isEditingEdi() {
            return this.model.orderType === OrderTypes.EDIWholesale || this.disposalSwitch === 1;
        },
        localWarehouses() {
            return [...this.warehouses];
        },
    },
    watch: {
        businessOrder(val) {
            this.model = Object.assign({
                warehouseId: null,
            }, val);
        },
    },
    async mounted() {
        await this.loadSettings();
        await this.prepareModel();

        /* eslint-disable */
        const fields = [
            { element: "consignee-address-line-1", field: "Address1", mode: so.fieldMode.SEARCH },
        ];
        options = {
            key: process.env.VUE_APP_SERVICE_OBJECTS_KEY,
            isTrial: false,
            setBiasToIP: true,
        };
        const DOTSGlobalAddressComplete = new so.Address(fields, options);
        DOTSGlobalAddressComplete.listen("populate", this.autoPopulateSelectedAddress);
    },
    methods: {
        findWarehouseAddress() {
            const selectedWarehouse = this.warehouses.find(w => w.warehouseId === this.model.warehouseId);
            const address = {
                name: selectedWarehouse.warehouseName,
                address1: selectedWarehouse.warehouseStreetAddress,
                city: selectedWarehouse.warehouseCity,
                country: selectedWarehouse.warehouseCountry,
                postalCode: selectedWarehouse.warehousePostalCode,
                state: selectedWarehouse.warehouseState,
            };

            return address;
        },
        changeDisposalSwitch({ value }) {
            this.disposalSwitch = value;
            if (value === 1) {
                const address = this.findWarehouseAddress();
                this.populateSelectedAddress(address)
            }
            else {
                this.populateSelectedAddress({})
            }
        },
        autoPopulateSelectedAddress(address, variations) {
            this.model.streetAddress1 = address.Address1;
            if (this.showSecondAddressLine) this.model.streetAddress2 = address.Address2;
            this.model.city = address.Locality;
            this.model.stateOrProvince = address.AdminArea;
            this.model.zipCode = address.PostalCode;
            this.model.country = address.CountryIso2;
        },
        populateSelectedAddress(address) {
            this.model.customerName = address.name;
            this.model.streetAddress1 = address.address1;
            this.model.city = address.city;
            this.model.stateOrProvince = address.state;
            this.model.zipCode = address.postalCode;
            this.model.country = address.country;
        },
        prepareModel(businessOrder) {
            const model = Object.assign({}, businessOrder);
            const items = model.items ? [...model.items] : [];
            const id = this.genId();

            if (!businessOrder) {
                model.id = id;
                model.referenceNumber = null;
            }

            this.initialSearchItems = Object.assign({});
            model.isSmartFreightSelected = this.isSmartFreightSelected;
            model.items = items;

            if (!model.country) {
                model.country = 'US';
            }

            const defaultWarehouseId = this.warehouses[0].warehouseId;
            this.handleWarehouseSelection(defaultWarehouseId)
            model.warehouseId = defaultWarehouseId;
            model.warehouseName = this.warehouses[0].warehouseName;
            model.isUnavailableExpiredLotOrder = true;
            return model;
        },
        async loadSettings() {
            const [countries, settings, warehouses] = await Promise.all([
                this.$monitor(() => countriesService.getAll()),
                this.$monitor(() => settingsService.getAll()),
                this.$monitor(() => warehouseService.getAll()),
            ]);

            if (!countries) {
                this.$emit('cancel');
                return;
            }

            this.countryCodes = countries || this.countryCodes;
            this.shipperContactInformation = settings.shipperContactInformation;
            this.warehouses.forEach(w => {
                const warehouse = warehouses.find(u => u.id === w.warehouseId);
                if (warehouse) {
                    w.warehouseCity = warehouse.warehouseCity;
                    w.warehouseCountry = warehouse.warehouseCountry;
                    w.warehousePostalCode = warehouse.warehousePostalCode;
                    w.warehouseState = warehouse.warehouseState;
                    w.warehouseStreetAddress = warehouse.warehouseStreetAddress;
                    w.warehouseTimeZone = warehouse.warehouseTimeZone;
                }
            });
        },
        addAddressLine() {
            this.showSecondAddressLine = true;
        },
        genId() {
            if (!this.id) {
                this.id = 0;
            }

            this.id += 1;

            return this.id;
        },
        async reValidate() {
            const { form } = this.$refs;
            this.$nextTick(() => form.validate({ silent: true }));
            if (!form.hasError) {
                return;
            }
            form.clearError();
        },
        async save() {
            const isValid = await this.$refs.form.validate();
            if (!isValid) {
                return;
            }

            if(this.disposalSwitch === 1) {
                if( this.model.orderComments === null) {
                    this.$toast.error($t('businessOrders.toasts.orderCommentsMissing'));
                    return;
                }

                const disposalText = $t('businessOrders.labels.disposalOrderComment') + this.model.orderComments;
                this.model.orderComments = disposalText;
            }

            if (this.model.country !== 'US') {
                if (this.model.items.some(x => x.hsCodeId === null)) {
                    this.$toast.error($t('businessOrders.toasts.itemsHSCodesMissing'));
                    return;
                }

                if (this.shipperContactInformation === null
                    || this.shipperContactInformation.shipperEmailAddress === null
                    || this.shipperContactInformation.shipperPhoneNumber === null) {
                    this.$toast.error($t('businessOrders.toasts.shipperContactInfoMissing'));
                    return;
                }
            }

            const businessOrder = sanitize(this.model);
            this.$emit('save', businessOrder);
        },
        async cancel() {
            const confirmed = await this.askConfirmation();

            if (confirmed) {
                this.$emit('cancel');
            }
        },
        convert() {
            this.$emit('convert');
        },
        getFormControl() {
            return this.$refs.form;
        },

        async fileUploaded(items) {
            this.model.items = this.model.items.concat(items);

            await this.validate(this.model.items);

            if (this.model.items.length === 0) {
                this.$toast.error($t('items.bulk.error.noValidSkus'));
            }
        },
        saveSearchResult(loadedOptions) {
            this.initialSearchItems = loadedOptions;
        },
        async addNewItem(item) {
            if (this.model.items.some(x => x.sku === item.sku)) {
                this.$toast.error($t('items.toasts.skuAlreadyAdded', { sku: item.sku }));
                return;
            }

            this.model.items.push({ idx: this.genId(), ...item });
            await this.validate(this.model.items);
        },
        onOpenEditItem(item) {
            this.itemForEdit = { ...item };
        },
        cancelEditingItem() {
            this.itemForEdit = null;
        },
        async saveEditingItem(item) {
            const existingItem = this.model.items.find(i => i.sku === item.sku && i.lotNumber === item.lotNumber);
            if (existingItem === null) {
                this.itemForEdit = null;
                return;
            }

            if (item.errorMessage === $t('items.bulk.error.skuDoesNotExist')
                && existingItem.sku !== item.sku) {
                // Clear error message when SKU is edited
                // eslint-disable-next-line
                item.errorMessage = null;
            }

            const idx = this.model.items.indexOf(existingItem);
            this.model.items[idx] = item;
            this.itemForEdit = null;
            await this.validate(this.model.items);
        },
        async deleteItem(item) {
            const idx = this.model.items.indexOf(item);
            if (idx === -1) {
                return;
            }

            this.model.items.splice(idx, 1);
            await this.validate(this.model.items);
        },
        async validate(items) {
            const result = await validator.validate(items);
            return this.setValidationResults(result);
        },
        setValidationResults(result = []) {
            this.validity = result;
            this.invalidItemsCount = result.reduce((acc, item) => ((item.$valid) ? acc : acc + 1), 0);
            return !this.invalidItemsCount;
        },
        async handleWarehouseSelection(value) {   
            if (!this.model) {
                this.model = {};
            }

            this.model.warehouseId = value;
            if (value === -1) {
                this.selectedWarehouseName = null;
            }
            else {
                const selectedWarehouse = this.warehouses.find(x => x.warehouseId === value);
                this.selectedWarehouseName = selectedWarehouse.warehouseName;                               
            }

            if (this.disposalSwitch === 1) {
                const address = this.findWarehouseAddress();
                this.populateSelectedAddress(address)
            }

            this.model.warehouseName = this.selectedWarehouseName;
            const itemList = this.findItemByWarehouse(value).map(item => ({
                    sku: item.itemId,
                    quantity: item.quantity,
                    description: item.description,
                    availableQty: item.quantity,
                    isBackOrdered: false,
                    lotNumber: item.lotNumber,
                    expirationDate: item.expirationDate,
                    isLotTracked: true,
                    errorMessage: null,
                    availableInventories: null,
            }));
            this.model.items = itemList;
            await this.validate(this.model.items);
        },
        findItemByWarehouse(warehouseId) {
            return this.expiredItems.filter(x => x.warehouseId === warehouseId);
        },
        askManualSelectionConfirmation() {
            return new Promise((resolve) => {
                this.$modal.show(BusinessOrderManualSelectionModal, { orderId: this.orderId }, {
                    name: 'businessOrderManualSelectionModal',
                    height: 'auto',
                    width: 580,
                    adaptive: true,
                    scrollable: true,
                    clickToClose: false,
                }, {
                    select:() => resolve(true),
                    closed: () => resolve(false),
                });
            });
        },
        askInsufficientInventoryConfirmation() {
            return new Promise((resolve) => {
                this.$dialog.info({
                    title: $t('businessOrders.warehouse.insufficientInventoryTitle'),
                    text: $t('businessOrders.warehouse.insufficientInventoryText'),
                    buttons: [
                        { title: $t('general.cancel') },
                        { title: $t('businessOrders.warehouse.insufficientInventoryProceed'), handler: () => resolve(true), close: true },
                    ],
                }, {}, {
                    closed: () => resolve(false),
                });
            });
        },
        askConfirmation() {
            if (this.model.items.length === 0) {
                return Promise.resolve(true);
            }

            return new Promise((resolve) => {
                this.$dialog.info({
                    text: $t('businessOrders.warnings.modalClose'),
                    buttons: [
                        { title: $t('general.cancel') },
                        { title: $t('general.ok'), handler: () => resolve(true), close: true },
                    ],
                }, {}, {
                    closed: () => resolve(false),
                });
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.expired-lot-business-order-create-form {
    &__disclaimer {
        margin-top: 5px;
        margin-bottom: 50px;
    }

    &__contorl {
        display: flex;
        align-items: center;
        justify-content: center;
    }
}

.col {
    &--50-percent {
        max-width: 50%;
        padding-right: 10px;
    }
}

.sku-actions {
    margin: 15px 0;
}

.items-selector-container {
    width: calc(100% - 140px);

    &-narrow {
        width: calc(100% - 170px);
    }
}

.selected-item-info {
    margin-bottom: 20px;

    .item-info-section {
        margin: 0 5px;

        .info-section-label {
            display: inline-block;
            padding: 8px 0;
            color: $color-gray-400;
            font-size: $font-size-sm;
            font-weight: 500;
            line-height: $line-height-base;
            text-transform: uppercase;
        }

        .info-section-text {
            width: 100%;
            min-height: $input-height;
            padding: 9px 13px;
            border-radius: $input-border-radius;
            background-color: $color-teal-950;
            color: $color-gray-200;
            font-size: $font-size-base;
            word-break: break-word;

            &.disabled {
                border-color: $color-gray-800;
                background-color: $color-gray-800;
                cursor: not-allowed;
            }
        }
    }

    .item-empty-space {
        flex-grow: 0;
        min-width: 20px;
    }
}
</style>
